import { object } from "yup";
import dayjs from "dayjs";
import { useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useIdentity } from "../IdentityProvider";

const today = dayjs().startOf("day");
const earliestDate = new Date("January 1, 2023");
const earliestDay = dayjs(earliestDate);

const NotificationsExportFormSchema = object({
  fromDate: object()
    .required("You must provide a date to filter to begin the export on.")
    .test(
      "is a date",
      "You must input a properly formatted date.",
      // @ts-ignore
      (fromDate: dayjs.Dayjs | null) => {
        return !!fromDate && fromDate.isValid();
      }
    )
    .test(
      "no future from date",
      "This date cannot be in the future.",
      // @ts-ignore
      (fromDate: dayjs.Dayjs | null) => {
        if (!fromDate) {
          return false;
        }
        return fromDate.isBefore(today) || fromDate.isSame(today);
      }
    )
    .test(
      "no earlier than date",
      "This date cannot be this far into the past.",
      // @ts-ignore
      (fromDate: dayjs.Dayjs | null) => {
        if (!fromDate) {
          return false;
        }
        return fromDate.isAfter(earliestDate) || fromDate.isSame(earliestDate);
      }
    ),
  toDate: object()
    .required("You must provide a date to filter to end the export on.")
    .test(
      "is a date",
      "You must input a properly formatted date.",
      // @ts-ignore
      (toDate: dayjs.Dayjs | null) => {
        return !!toDate && toDate.isValid();
      }
    )
    .test(
      "no future to date",
      "This date cannot be in the future.",
      // @ts-ignore
      (toDate: dayjs.Dayjs | null) => {
        if (!toDate) {
          return false;
        }
        return toDate.isBefore(today) || toDate.isSame(today);
      }
    )
    .test(
      "no earlier than date",
      "This date cannot be before your filter start date.",
      // @ts-ignore
      (toDate: dayjs.Dayjs | null, ctx) => {
        if (!toDate) {
          return false;
        }
        const fromDate = ctx.parent.fromDate;
        return toDate.isAfter(fromDate) || toDate.isSame(fromDate);
      }
    ),
});
type FormState = ReturnType<typeof NotificationsExportFormSchema.validateSync>;

export default function NotificationsExportForm({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const identity = useIdentity();

  const formik = useFormik({
    initialValues: {
      fromDate: earliestDay,
      toDate: today,
    },
    onSubmit: (values: FormState) => {
      if (!identity.isAuthenticated) return;
      console.debug(values);
      // TODO: "REMEMBER THESE ARE DAYJS VALUES THAT NEED TO BE CONVERTED BEFORE SENDING"!
      // TODO: We will need to get all notifications during that time, not just one page...
      return new Promise((resolve) => {
        setTimeout(() => {
          formik.setStatus(
            "An internal server error occurred.  Please try again later."
          );
        }, 1000);
      }).then(() => {
        onSubmit();
        formik.setSubmitting(false);
      });
    },
    validationSchema: NotificationsExportFormSchema,
  });

  return (
    <Dialog open={open}>
      <DialogTitle>Export Monitoring Issues</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={3} pl={2} pr={2}>
          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            {formik.status && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {formik.status}
              </Alert>
            )}

            <Grid item xs={12} mt={2}>
              <FormControl sx={{ width: "100%" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From date"
                    onChange={(newValue) => {
                      formik.setFieldTouched("fromDate", true);
                      if (!!newValue && newValue.isValid()) {
                        formik.setFieldValue("fromDate", newValue);
                      }
                    }}
                    value={dayjs(formik.values.fromDate as any)}
                    minDate={earliestDay}
                    maxDate={today}
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                  />
                </LocalizationProvider>

                {formik.errors.fromDate && formik.touched.fromDate && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {String(formik.errors.fromDate)}
                  </Alert>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} mt={2}>
              <FormControl sx={{ width: "100%" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To date"
                    onChange={(newValue) => {
                      formik.setFieldTouched("toDate", true);
                      if (!!newValue && newValue.isValid()) {
                        formik.setFieldValue("toDate", newValue);
                      }
                    }}
                    value={dayjs(formik.values.toDate as any)}
                    minDate={earliestDay}
                    maxDate={today}
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                  />
                </LocalizationProvider>

                {formik.errors.toDate && formik.touched.toDate && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {String(formik.errors.toDate)}
                  </Alert>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} mt={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      formik.setStatus(undefined);
                      formik.resetForm(); // Hopefully unnecessary when there's a real backend
                      onClose();
                    }}
                    // TODO: Disable on cancel when there's a real backend...
                  >
                    Cancel
                  </Button>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="contained"
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    Export
                  </Button>
                </Box>
              </Box>
            </Grid>
          </form>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
